import * as React from 'react';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
import ConfirmDevice from 'auth/components/ConfirmDevice';
import ToastActions from 'actions/ToastActions';
// @ts-ignore
import Profile from 'entities/Profile';

import { getPersistFromCookie, setPersistCookie } from './cookieUtils';
import EnterPassword from './EnterPassword';
import EnterUid from './EnterUid';

import '../styles/SignInForm.scss';
// @ts-ignore
import { isIntendedCoachingUser } from 'setup/utils/setupCriteria';
import EventTracking from 'lib/EventTracking';

const mediumOfUid = (uid: string) => (uid.includes('@') ? 'email' : 'phone');

type Props = {
  initialUid?: string;
  emailConfirmationToken?: string;
};

type State = {
  signingIn: boolean;
  needsConfirmationErrorCode?: string;
  uid?: string;
  password?: string;
  medium: 'email' | 'phone';
  persist: boolean;
  needsPassword: boolean;
  page: 'uid' | 'password' | 'confirmation';
};

class LogInOrSignUpForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const uid = props.initialUid || '';

    this.state = {
      signingIn: false,
      needsPassword: false,
      uid,
      password: '',
      medium: mediumOfUid(uid),
      persist: getPersistFromCookie(),
      page: 'uid',
    };
  }

  trackCoachingUser(newUser: boolean, email?: string) {
    const trackingKey = newUser ? "signup_experiment.old_signup_flow.new_user" : "signup_experiment.old_signup_flow.existing_user";
    EventTracking.trackKey(trackingKey, {
        data: JSON.stringify({
          email,
    })
   })
 }

  togglePersistChange = () => {
    const newPersistValue = !this.state.persist;
    setPersistCookie(newPersistValue);
    this.setState({ persist: newPersistValue });
  };

  handlePasswordChange = (password?: string) => {
    this.setState({ password });
  };

  handleUidChange = (uid?: string) => {
    if (!uid) this.setState({ uid });
    else this.setState({ uid, medium: mediumOfUid(uid) });
  };

  handleSubmitUid = async () => {
    const { uid } = this.state;
    // No sense in checking if this is an existing account if the input is only whitespace
    if (!uid || !uid.trim()) {
      return this.handleInvalidCredentials();
    }
    try {
      const profile: Profile = await Profile.fetchByIdentifier(uid);
      if (!!profile && isIntendedCoachingUser()) {
        this.trackCoachingUser(false, uid);
      }
      if (profile.needsPassword()) {
        // if an account exists and it needs a password
        // send them the confirmation email and redirect them to an instruction page
        this.createCookieAndConfirmDevice(true);
      } else {
        this.setState({ page: 'password' });
      }
    } catch {
      if (isIntendedCoachingUser()) {
        this.trackCoachingUser(true, uid);
      }
      this.createCookieAndConfirmDevice();
    }
  };

  createCookieAndConfirmDevice = async (needsPassword: boolean = false) => {
    await AuthActions.createConfirmationCookie(this.state.uid);
    this.setState({ page: 'confirmation', needsPassword });
  };

  handleInvalidCredentials = (err?: any) => {
    const uidErr = 'Invalid email address or phone number';
    const unregistered = err && err.statusCode === 404;
    const errorMessage = unregistered ? uidErr : `${uidErr} or password`;

    ToastActions.showErrorToast(errorMessage);
    this.setState({ signingIn: false });
  };

  handleSubmitPassword = () => {
    const { uid, password, persist } = this.state;
    const { emailConfirmationToken } = this.props;
    if (this.state.signingIn || !uid || !password) return;

    AuthActions.signIn(uid, password, emailConfirmationToken, persist).catch((err: any) => {
      if (err && err.statusCode === 403) {
        this.setState({ page: 'confirmation', needsConfirmationErrorCode: err.code });
      } else {
        ToastActions.showErrorToast('There was an error logging in. Please try again later.');
        this.setState({ signingIn: false });
      }
    });

    this.setState({ signingIn: true });
  };

  render() {
    const { uid, needsPassword, medium, needsConfirmationErrorCode, page } = this.state;
    return page === 'confirmation' && uid ? (
      <ConfirmDevice
        deviceType={medium}
        uid={uid}
        needsPassword={needsPassword}
        onGoBack={() => {
          const page = this.state.password ? 'password' : 'uid';
          this.setState({ page, signingIn: false });
        }}
        needsConfirmationErrorCode={needsConfirmationErrorCode}
      />
    ) : page === 'password' ? (
      <EnterPassword
        uid={uid}
        onPasswordChange={this.handlePasswordChange}
        submitIsDisabled={this.state.signingIn || !this.state.password}
        onSubmit={this.handleSubmitPassword}
        persist={this.state.persist}
        togglePersistChange={this.togglePersistChange}
        onGoBack={() => {
          this.setState({ page: 'uid', signingIn: false });
        }}
      />
    ) : (
      <EnterUid
        initialUid={this.props.initialUid || uid || ''}
        onUidChange={this.handleUidChange}
        submitIsDisabled={!this.state.uid}
        onSubmit={this.handleSubmitUid}
        emailConfirmationToken={this.props.emailConfirmationToken}
      />
    );
  }
}
export default LogInOrSignUpForm;
